export interface DesktopBackendStatus {
  running: boolean
  pid?: number | null
}

export interface StartupProgressStep {
  key: string
  label: string
  phase: 'launcher' | 'api'
  status: 'pending' | 'running' | 'done' | 'failed'
  detail?: string
}

export interface StartupProgress {
  version?: number
  started_at?: number
  finished: boolean
  current_step: string | null
  current_step_index: number
  steps: StartupProgressStep[]
  error?: string | null
}

export const EMPTY_STARTUP_PROGRESS: StartupProgress = {
  finished: false,
  current_step: null,
  current_step_index: -1,
  steps: [],
  error: null,
}

export interface DesktopRuntimeStatus {
  state: string
  last_error?: string | null
  ports: {
    postgres: number
    redis: number
    api: number
  }
  services: Record<string, {
    configured: boolean
    running: boolean
    healthy: boolean
    pid: number | null
    return_code: number | null
  }>
  runtime_file: string
  timestamp: number
}

export interface DesktopSidecarRuntimeStatus {
  state: string
  last_error?: string | null
  ports: {
    postgres: number
    redis: number
  }
  services: Record<string, {
    configured: boolean
    running: boolean
    healthy: boolean
    pid: number | null
    error?: string | null
  }>
  timestamp: number
}

export interface DesktopDiagnosticsExportResult {
  file_path: string
  timestamp: number
}

export interface DesktopVersionInfo {
  version: string
  deploy_mode: string
  python_version: string
}

export interface DesktopBridgeError {
  code: string
  message: string
  details?: Record<string, unknown> | null
}

export interface DesktopErrorMetrics {
  total_errors: number
  by_code: Record<string, number>
}

export interface DesktopUpdateComponent {
  name: string
  needUpdate?: boolean | null
  required?: boolean | null
  currentVersion?: string | null
  targetVersion?: string | null
  minVersion?: string | null
  manifestUrl?: string | null
  fullPackageUrl?: string | null
  patchUrl?: string | null
  patchType?: string | null
  sha256?: string | null
  size?: number | null
  notes?: string | null
}

export interface DesktopUpdateCheckResult {
  appCode?: string | null
  platform?: string | null
  arch?: string | null
  channel?: string | null
  hasUpdate?: boolean | null
  forceUpdate?: boolean | null
  currentVersion?: string | null
  latestVersion?: string | null
  minVersion?: string | null
  releaseNotes?: string | null
  shell?: DesktopUpdateComponent | null
  components?: DesktopUpdateComponent[] | null
}

export interface DesktopUpdateState {
  shellVersion: string
  runtimeRoot: string
  stateFile: string
  components: Record<string, string>
  updatedAt?: number | null
}

export interface DesktopUpdateApplyResult {
  component: string
  version: string
  installedPath: string
  files: number
}

type TauriInvokeFn = (command: string, args?: Record<string, unknown>) => Promise<unknown>
const desktopBridgeMode = process.env.NEXT_PUBLIC_DESKTOP_BRIDGE ?? 'tauri'

declare global {
  interface Window {
    __TAURI_INTERNALS__?: {
      invoke?: TauriInvokeFn
    }
    __TAURI__?: {
      core?: {
        invoke?: TauriInvokeFn
      }
      tauri?: {
        invoke?: TauriInvokeFn
      }
    }
  }
}

function getTauriInvoke(): TauriInvokeFn | null {
  if (desktopBridgeMode !== 'tauri') {
    return null
  }
  if (typeof window === 'undefined') {
    return null
  }

  const internalInvoke = window.__TAURI_INTERNALS__?.invoke
  if (typeof internalInvoke === 'function') {
    return internalInvoke.bind(window.__TAURI_INTERNALS__)
  }

  const tauriBridge = window.__TAURI__

  const coreBridge = tauriBridge?.core
  const coreInvoke = coreBridge?.invoke
  if (typeof coreInvoke === 'function') {
    return coreInvoke.bind(coreBridge)
  }

  const legacyTauriBridge = tauriBridge?.tauri
  const tauriInvoke = legacyTauriBridge?.invoke
  return typeof tauriInvoke === 'function'
    ? tauriInvoke.bind(legacyTauriBridge)
    : null
}

export function isDesktopBridgeAvailable(): boolean {
  return getTauriInvoke() !== null
}

export async function desktopCall<T>(method: string, params: Record<string, unknown> = {}): Promise<T> {
  const invoke = getTauriInvoke()
  if (!invoke) {
    throw new Error('Tauri desktop bridge is not available')
  }

  return await invoke('desktop_call', {
    method,
    params,
  }) as T
}

export async function getDesktopBackendStatus(): Promise<DesktopBackendStatus> {
  const invoke = getTauriInvoke()
  if (!invoke) {
    return { running: false, pid: null }
  }
  return await invoke('desktop_backend_status') as DesktopBackendStatus
}

/**
 * Read the desktop startup progress file. Uses the Tauri command when
 * available (works before the API is up); otherwise falls back to polling
 * the HTTP endpoint (only useful once the API accepts connections).
 */
export async function readStartupProgress(): Promise<StartupProgress> {
  const invoke = getTauriInvoke()
  if (invoke) {
    try {
      const result = await invoke('desktop_read_startup_progress') as StartupProgress
      return { ...EMPTY_STARTUP_PROGRESS, ...(result || {}) }
    } catch {
      return EMPTY_STARTUP_PROGRESS
    }
  }
  return EMPTY_STARTUP_PROGRESS
}

/**
 * Poll /agent-api/startup/progress over HTTP.
 *
 * IMPORTANT: does NOT use getApiUrl() — that path goes through fetchConfig()
 * which loops up to 60×1s retrying while the API/DB is coming up, which would
 * freeze the startup progress bar. Instead it resolves the base URL the same
 * way @/lib/config does at build time and applies a short timeout.
 */
export async function readStartupProgressHttp(): Promise<StartupProgress> {
  try {
    const { runtimeTarget } = await import('@/lib/runtime-target')
    const envDesktop = process.env.NEXT_PUBLIC_DESKTOP_API_URL
    const envApi = process.env.NEXT_PUBLIC_API_URL
    const fallback = runtimeTarget === 'desktop'
      ? 'http://127.0.0.1:50505'
      : 'http://localhost:5055'
    const baseUrl = runtimeTarget === 'desktop'
      ? (envDesktop || envApi || fallback)
      : (envApi || fallback)

    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), 3000)
    try {
      const resp = await fetch(`${baseUrl}/agent-api/startup/progress`, {
        cache: 'no-store',
        signal: controller.signal,
      })
      if (!resp.ok) return EMPTY_STARTUP_PROGRESS
      const data = await resp.json()
      return { ...EMPTY_STARTUP_PROGRESS, ...(data || {}) }
    } finally {
      clearTimeout(timer)
    }
  } catch {
    return EMPTY_STARTUP_PROGRESS
  }
}

export async function restartDesktopBackend(): Promise<DesktopBackendStatus> {
  const invoke = getTauriInvoke()
  if (!invoke) {
    throw new Error('Tauri desktop bridge is not available')
  }
  return await invoke('desktop_backend_restart') as DesktopBackendStatus
}

export async function getDesktopRuntimeStatus(): Promise<DesktopRuntimeStatus> {
  return await desktopCall<DesktopRuntimeStatus>('system.runtime.status')
}

export async function startDesktopRuntime(startMiddleware: boolean = true): Promise<DesktopRuntimeStatus> {
  return await desktopCall<DesktopRuntimeStatus>('system.runtime.start', {
    start_middleware: startMiddleware,
  })
}

export async function stopDesktopRuntime(): Promise<DesktopRuntimeStatus> {
  return await desktopCall<DesktopRuntimeStatus>('system.runtime.stop')
}

export async function getDesktopSidecarStatus(): Promise<DesktopSidecarRuntimeStatus> {
  const invoke = getTauriInvoke()
  if (!invoke) {
    return {
      state: 'unavailable',
      last_error: 'Tauri desktop bridge is not available',
      ports: { postgres: 0, redis: 0 },
      services: {},
      timestamp: Date.now(),
    }
  }
  return await invoke('desktop_sidecar_status') as DesktopSidecarRuntimeStatus
}

export async function startDesktopSidecars(): Promise<DesktopSidecarRuntimeStatus> {
  const invoke = getTauriInvoke()
  if (!invoke) {
    throw new Error('Tauri desktop bridge is not available')
  }
  return await invoke('desktop_sidecar_start') as DesktopSidecarRuntimeStatus
}

export async function stopDesktopSidecars(): Promise<DesktopSidecarRuntimeStatus> {
  const invoke = getTauriInvoke()
  if (!invoke) {
    throw new Error('Tauri desktop bridge is not available')
  }
  return await invoke('desktop_sidecar_stop') as DesktopSidecarRuntimeStatus
}

export async function exportDesktopDiagnostics(): Promise<DesktopDiagnosticsExportResult> {
  const invoke = getTauriInvoke()
  if (!invoke) {
    throw new Error('Tauri desktop bridge is not available')
  }
  return await invoke('desktop_export_diagnostics') as DesktopDiagnosticsExportResult
}

export function parseDesktopBridgeError(error: unknown): DesktopBridgeError {
  const fallback: DesktopBridgeError = {
    code: 'UNKNOWN_DESKTOP_ERROR',
    message: error instanceof Error ? error.message : String(error),
    details: null,
  }

  const rawMessage = error instanceof Error ? error.message : String(error)
  try {
    const parsed = JSON.parse(rawMessage) as Partial<DesktopBridgeError>
    if (typeof parsed.code === 'string' && typeof parsed.message === 'string') {
      return {
        code: parsed.code,
        message: parsed.message,
        details: (parsed.details as Record<string, unknown> | null | undefined) ?? null,
      }
    }
  } catch {
    // Ignore parsing errors and return fallback
  }
  return fallback
}

// ── Browser types & commands ───────────────────────────────────────────────

export interface BrowserSessionInfo {
  session_id: string
  env_id: string
  fingerprint_id: string
  proxy_id: string
  pid: number
  status: string
  started_at: number
  /** Chrome DevTools Protocol 调试端口（供扩展/Agent CDP 驱动） */
  debug_port?: number | null
}

export async function startBrowserSession(
  envId: string,
  fingerprintId: string,
  proxyId: string,
): Promise<BrowserSessionInfo> {
  const invoke = getTauriInvoke()
  if (!invoke) {
    throw new Error('Tauri desktop bridge is not available')
  }
  return await invoke('desktop_browser_start', {
    env_id: envId,
    fingerprint_id: fingerprintId,
    proxy_id: proxyId,
  }) as BrowserSessionInfo
}

export async function stopBrowserSession(sessionId: string): Promise<BrowserSessionInfo> {
  const invoke = getTauriInvoke()
  if (!invoke) {
    throw new Error('Tauri desktop bridge is not available')
  }
  return await invoke('desktop_browser_stop', {
    session_id: sessionId,
  }) as BrowserSessionInfo
}

export async function getBrowserStatus(sessionId: string): Promise<BrowserSessionInfo | null> {
  const invoke = getTauriInvoke()
  if (!invoke) {
    throw new Error('Tauri desktop bridge is not available')
  }
  return await invoke('desktop_browser_status', {
    session_id: sessionId,
  }) as BrowserSessionInfo | null
}

export async function listBrowserSessions(): Promise<BrowserSessionInfo[]> {
  const invoke = getTauriInvoke()
  if (!invoke) {
    throw new Error('Tauri desktop bridge is not available')
  }
  return await invoke('desktop_browser_list') as BrowserSessionInfo[]
}

export async function systemOpen(path: string): Promise<void> {
  const invoke = getTauriInvoke()
  if (!invoke) {
    throw new Error('Tauri desktop bridge is not available')
  }
  await invoke('desktop_system_open', { path })
}

/**
 * 通过桌面系统目录选择器挑选一个本地目录。
 * 用户取消时返回 null；非桌面环境直接抛错。
 */
export async function pickDesktopDirectory(): Promise<string | null> {
  const invoke = getTauriInvoke()
  if (!invoke) {
    throw new Error('Tauri desktop bridge is not available')
  }
  return await invoke('desktop_pick_directory') as string | null
}

export async function getDesktopErrorMetrics(): Promise<DesktopErrorMetrics> {
  const invoke = getTauriInvoke()
  if (!invoke) {
    return {
      total_errors: 0,
      by_code: {},
    }
  }
  return await invoke('desktop_error_metrics') as DesktopErrorMetrics
}

export async function getDesktopUpdateState(): Promise<DesktopUpdateState> {
  const invoke = getTauriInvoke()
  if (!invoke) {
    return {
      shellVersion: 'unknown',
      runtimeRoot: '',
      stateFile: '',
      components: {},
      updatedAt: null,
    }
  }
  return await invoke('desktop_update_state') as DesktopUpdateState
}

export async function checkDesktopUpdate(
  endpoint: string,
  appCode: string = 'fishown-agent-desktop',
  channel: string = 'stable',
): Promise<DesktopUpdateCheckResult> {
  const invoke = getTauriInvoke()
  if (!invoke) {
    throw new Error('Tauri desktop bridge is not available')
  }
  return await invoke('desktop_update_check', {
    endpoint,
    appCode,
    channel,
  }) as DesktopUpdateCheckResult
}

export async function getDesktopVersion(): Promise<DesktopVersionInfo> {
  const invoke = getTauriInvoke()
  if (!invoke) {
    return { version: 'unknown', deploy_mode: 'server', python_version: '' }
  }
  return await desktopCall<DesktopVersionInfo>('system.version')
}

// ── Sub-app launcher ──────────────────────────────────────────────────────

export interface SubAppLaunchResult {
  pid: number
  app_id: string
  api_url: string
  status: string
}

export async function launchDesktopSubapp(
  appId: string,
  appPath: string,
  launchInfo?: Record<string, unknown>,
): Promise<SubAppLaunchResult> {
  const invoke = getTauriInvoke()
  if (!invoke) {
    throw new Error('Tauri desktop bridge is not available')
  }
  return await invoke('desktop_launch_subapp', {
    app_id: appId,
    app_path: appPath,
    launchInfo,
  }) as SubAppLaunchResult
}

export async function applyDesktopComponentUpdate(
  manifestUrl: string,
): Promise<DesktopUpdateApplyResult> {
  const invoke = getTauriInvoke()
  if (!invoke) {
    throw new Error('Tauri desktop bridge is not available')
  }
  return await invoke('desktop_update_apply_component', {
    manifestUrl,
  }) as DesktopUpdateApplyResult
}

// ── Default paths ────────────────────────────────────────────────────────────

export interface DesktopDefaultPaths {
  materials: string
  notebook: string
}

export async function getDesktopDefaultPaths(): Promise<DesktopDefaultPaths> {
  const invoke = getTauriInvoke()
  const authInfo = getAuthInfoFromToken()
  const tenantCode = authInfo?.tenant_code || ''
  if (!invoke) {
    const base = '~/Documents/即策笔记/资源库'
    const nb = '~/Documents/即策笔记/笔记本'
    return {
      materials: tenantCode ? base + '/' + tenantCode : base,
      notebook: tenantCode ? nb + '/' + tenantCode : nb,
    }
  }
  return await invoke('desktop_get_default_paths', { tenantCode }) as DesktopDefaultPaths
}

// ── Athena API desktop bridge ─────────────────────────────────────────────────

export interface AthenaAuthInfo {
  id_customer?: number
  tenant_code?: string
  space_code?: string
  space_owner_id?: number
  /** Full JWT as stored in localStorage auth-storage. Single source of truth. */
  token?: string
}

/**
 * Decode JWT token payload (no signature verification) to extract claims.
 * Used client-side to get auth info for desktop athena calls.
 */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const payload = parts[1]
    // Base64url decode
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(decoded)
  } catch {
    return null
  }
}

/**
 * Extract auth info from the stored JWT token claims.
 *
 * This is the single bridge that reads the desktop auth token out of
 * localStorage and hands it to the backend. Python can never read the webview
 * localStorage directly (process isolation), so the frontend reports the full
 * token here on every invoke — the backend reads it from the `_auth` param.
 */
function getAuthInfoFromToken(): AthenaAuthInfo | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem('auth-storage')
    if (!raw) return null
    const parsed = JSON.parse(raw)
    const token = parsed?.state?.token
    if (!token || token === 'not-required') return null
    const claims = decodeJwtPayload(token)
    if (!claims) return null
    return {
      id_customer: (claims.id_customer || claims.customerId || claims.sub) as number | undefined,
      tenant_code: (claims.tenant_code || claims.tenantCode) as string | undefined,
      space_code: claims.space_code as string | undefined,
      token,
    }
  } catch {
    return null
  }
}

/**
 * Call athena-api logic via desktop bridge (CLI binary, no HTTP).
 *
 * Maps frontend athena-api paths like "workflow.create" to desktop
 * bridge method "athena.workflow.create".
 */
export async function desktopAthenaCall<T = unknown>(
  athenaMethod: string,
  params: Record<string, unknown> = {},
): Promise<T> {
  const authInfo = getAuthInfoFromToken()
  const callParams: Record<string, unknown> = { ...params }
  if (authInfo) {
    callParams._auth = authInfo
  }
  return await desktopCall<T>(`athena.${athenaMethod}`, callParams)
}
